/**
 * Type definitions for the voice assistant integration
 */

/**
 * State machine states
 */
export enum AssistantState {
  IDLE = 'idle',
  LISTENING = 'listening',
  PROCESSING = 'processing',
  SPEAKING = 'speaking',
  INTERRUPTED = 'interrupted'
}

/**
 * Message types for WebSocket communication
 */
export enum MessageType {
  // Client -> Server
  START_LISTENING = 'start_listening',
  STOP_LISTENING = 'stop_listening',
  AUDIO_DATA = 'audio_data',
  INTERRUPT = 'interrupt',
  
  // Server -> Client
  STATE_CHANGE = 'state_change',
  TRANSCRIPTION = 'transcription',
  TRANSCRIPTION_FINAL = 'transcription_final',
  ASSISTANT_MESSAGE = 'assistant_message',
  ASSISTANT_MESSAGE_CHUNK = 'assistant_message_chunk',
  AUDIO_RESPONSE = 'audio_response',
  AUDIO_RESPONSE_CHUNK = 'audio_response_chunk',
  ERROR = 'error'
}

/**
 * Base message interface
 */
export interface WebSocketMessage {
  type: MessageType;
  sessionId: string;
}

/**
 * Client -> Server: Audio data
 */
export interface AudioDataMessage extends WebSocketMessage {
  type: MessageType.AUDIO_DATA;
  data: string; // Base64-encoded audio chunk
  isFinal?: boolean;
}

/**
 * Client -> Server: Start/Stop/Interrupt
 */
export interface ControlMessage extends WebSocketMessage {
  type: MessageType.START_LISTENING | MessageType.STOP_LISTENING | MessageType.INTERRUPT;
}

/**
 * Server -> Client: State change
 */
export interface StateChangeMessage extends WebSocketMessage {
  type: MessageType.STATE_CHANGE;
  state: AssistantState;
}

/**
 * Server -> Client: Transcription
 */
export interface TranscriptionMessage extends WebSocketMessage {
  type: MessageType.TRANSCRIPTION | MessageType.TRANSCRIPTION_FINAL;
  text: string;
  isFinal: boolean;
}

/**
 * Server -> Client: Assistant message
 */
export interface AssistantMessageMessage extends WebSocketMessage {
  type: MessageType.ASSISTANT_MESSAGE | MessageType.ASSISTANT_MESSAGE_CHUNK;
  text: string;
  isFinal: boolean;
}

/**
 * Server -> Client: Audio response
 */
export interface AudioResponseMessage extends WebSocketMessage {
  type: MessageType.AUDIO_RESPONSE | MessageType.AUDIO_RESPONSE_CHUNK;
  data: string; // Base64-encoded audio
  isFinal: boolean;
}

/**
 * Server -> Client: Error
 */
export interface ErrorMessage extends WebSocketMessage {
  type: MessageType.ERROR;
  error: string;
}

/**
 * Voice settings configuration
 */
export interface VoiceSettings {
  voiceProvider: 'openai' | 'elevenlabs';
  voice: string;
  language: string;
  model: string;
}